import { useCallback } from 'react';

import { useGetLessonWhiteboardQuery } from 'core/graphql';
import { transformWhiteboard } from './transform';
import type { IWhiteboard } from './transform';

interface IUseLessonWhiteboard {
  whiteboard: IWhiteboard | null;
  loading: boolean;
  error: string;
  /** Re-reads the board from the server and returns the fresh view model. */
  refetchWhiteboard: () => Promise<IWhiteboard | null>;
}

/**
 * The per-lesson board. network-only: the scene is mutable server state
 * (another lesson linked to the same board may have changed it) — never
 * serve a stale cached scene into the editor.
 */
export const useLessonWhiteboard = (lessonId: string): IUseLessonWhiteboard => {
  const { data, loading, error, refetch } = useGetLessonWhiteboardQuery({
    fetchPolicy: 'network-only',
    variables: { lessonId },
    skip: !lessonId,
  });

  const refetchWhiteboard = useCallback(async () => {
    const result = await refetch();
    return transformWhiteboard(result.data);
  }, [refetch]);

  return {
    whiteboard: transformWhiteboard(data),
    loading,
    error: error?.message || '',
    refetchWhiteboard,
  };
};
